/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import bind from 'apmui/decorators/bind';
import { StatusCheck } from './StatusCheck';

interface IExpirableStatusCheckOptions {
    onDebugMsg?: (msg: string) => void;
    onComplete: () => void;
    onError: (httpErr: number, apmErr: number) => void;
    onProgress: (eventName: string) => boolean;
    onTimeout?: () => void;
    noConnectionTimeoutVal?: number;

    token: string;
    uri: string;
}

class ExpirableStatusCheck extends StatusCheck {
    completed: boolean;

    private options: IExpirableStatusCheckOptions;
    private noConnectionTimer: number | null;
    private retryCounter: number;
    private maxRetries: number;

    constructor(options: IExpirableStatusCheckOptions) {
        super();
        this.options = options;

        if (!this.options.noConnectionTimeoutVal) {
            this.options.noConnectionTimeoutVal = 20 * 1000; // make configurable?
        }

        this.noConnectionTimer = null;
        this.retryCounter = 0;
        this.maxRetries = 10;
        this.completed = false;
    }

    onComplete = () => {
        this.cancelTimeout();
        this.options.onComplete();
    };

    onProgress = (eventName: string) => {
        // if callback return true - that means that it is right time to
        // cancel "no connection" timeout
        if (this.options.onProgress(eventName)) {
            this.cancelTimeout();
        }
    };

    onError = (httpErr: number, apmErr: number) => {
        this.cancelTimeout();
        this.options.onError(httpErr, apmErr /* , this.maxRetries - this.retryCounter ??? */);
        this.retryGetCheckStatus();
    };

    interruptedByBrowser(): boolean {
        return this.xhrFinished() && !this.completed;
    }

    retryGetCheckStatus(timeout: number = 3000, restartTimeout: boolean = false): void {
        this.retryCounter++;
        if (this.retryCounter < this.maxRetries) {
            setTimeout(() => this.restart(restartTimeout), timeout);
            return;
        }
        this.CallDebugCallback('Tried to fetch status ' + this.maxRetries + ' times. Giving up');
        this.abort();
    }

    run(): void {
        super.run(this.options.uri);
    }

    abort(): void {
        this.cancelTimeout();
        super.abort();
        this.completed = false;
    }

    restart(restartTimeout: boolean): void {
        this.abort();
        this.run();
        if (restartTimeout) {
            this.startTimeout();
        }
    }

    startTimeout(): void {
        if (this.completed) {
            return;
        }
        if (this.noConnectionTimer) {
            return;
        }
        this.CallDebugCallback('startTimeout:' + this.options.token);
        this.noConnectionTimer = window.setTimeout(this.onNoConnectionTimeout, this.options.noConnectionTimeoutVal);
    }

    private CallDebugCallback(msg: string) {
        if (this.options.onDebugMsg) {
            this.options.onDebugMsg(msg);
        }
    }

    private cancelTimeout(): void {
        if (this.noConnectionTimer) {
            try {
                clearTimeout(this.noConnectionTimer);
            } catch (e) {
                //
            }
            this.noConnectionTimer = null;
        }
        this.completed = true;
    }

    @bind
    private onNoConnectionTimeout(): void {
        this.CallDebugCallback('onNoConnectionTimeout:' + this.options.token);
        if (this.options.onTimeout) {
            this.options.onTimeout();
        }
    }
}

export { ExpirableStatusCheck, IExpirableStatusCheckOptions };
